import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import useScript from '@magento/peregrine/lib/hooks/useScript';
import mergeOperations from '@magento/peregrine/lib/util/shallowMerge';
import defaultOperations from './googleReCaptchaConfig.gql';
var GOOGLE_RECAPTCHA_HEADER = 'X-ReCaptcha';
var GOOGLE_RECAPTCHA_URL = 'https://www.google.com/recaptcha/api.js';
/**
 * Returns props necessary to attach Google ReCaptcha V3 to a form.
 *
 * @function
 *
 * @param {String} props.currentForm - Form name to match GraphQl ReCaptchaFormEnum.
 * @param {String} props.formAction - Action name to use for logging in API.
 * @param {Object} [props.operations] - GraphQL operations to be run by the hook.
 *
 * @returns {GoogleReCaptchaProps}
 *
 * @example <caption>Importing into your project</caption>
 * import { useGoogleReCaptcha } from '@magento/peregrine/lib/hooks/useGoogleReCaptcha';
 */

export var useGoogleReCaptcha = function useGoogleReCaptcha(props) {
  var _configData$recaptcha, _configData$recaptcha2, _configData$recaptcha3, _configData$recaptcha4;

  var operations = mergeOperations(defaultOperations, props.operations);
  var currentForm = props.currentForm,
      formAction = props.formAction;

  var _useQuery = useQuery(operations.getReCaptchaV3ConfigQuery, {
    fetchPolicy: 'cache-and-network'
  }),
      configData = _useQuery.data,
      configError = _useQuery.error,
      configLoading = _useQuery.loading;

  if (!globalThis['recaptchaCallbacks']) {
    globalThis['recaptchaCallbacks'] = {};
  }

  var _useState = useState(globalThis.hasOwnProperty('grecaptcha')),
      _useState2 = _slicedToArray(_useState, 2),
      apiIsReady = _useState2[0],
      setApiIsReady = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isGenerating = _useState4[0],
      setIsGenerating = _useState4[1];

  var _useState5 = useState(null),
      _useState6 = _slicedToArray(_useState5, 2),
      widgetId = _useState6[0],
      setWidgetId = _useState6[1]; // Container Reference to be used for the GoogleReCaptcha component


  var _useState7 = useState(null),
      _useState8 = _slicedToArray(_useState7, 2),
      inlineContainer = _useState8[0],
      setInlineContainer = _useState8[1]; // callback to update container element ref in case of mount/unmount


  var updateInlineContainerRef = useCallback(function (node) {
    if (node !== null) {
      setInlineContainer(node);
    }
  }, []);
  var recaptchaBadge = configData !== null && configData !== void 0 && (_configData$recaptcha = configData.recaptchaV3Config) !== null && _configData$recaptcha !== void 0 && _configData$recaptcha.badge_position && configData.recaptchaV3Config.badge_position.length > 0 ? configData.recaptchaV3Config.badge_position : 'bottomright';
  var recaptchaKey = configData === null || configData === void 0 ? void 0 : (_configData$recaptcha2 = configData.recaptchaV3Config) === null || _configData$recaptcha2 === void 0 ? void 0 : _configData$recaptcha2.website_key;
  var recaptchaLang = configData === null || configData === void 0 ? void 0 : (_configData$recaptcha3 = configData.recaptchaV3Config) === null || _configData$recaptcha3 === void 0 ? void 0 : _configData$recaptcha3.language_code;
  var activeForms = (configData === null || configData === void 0 ? void 0 : (_configData$recaptcha4 = configData.recaptchaV3Config) === null || _configData$recaptcha4 === void 0 ? void 0 : _configData$recaptcha4.forms) || [];
  var isEnabled = !(configError instanceof Error) && recaptchaKey && recaptchaKey.length > 0 && activeForms.includes(currentForm); // Determine which type of badge should be loaded

  var isInline = recaptchaBadge === 'inline'; // Construct script url with configs

  var scriptUrl = new URL(GOOGLE_RECAPTCHA_URL);
  scriptUrl.searchParams.append('badge', recaptchaBadge); // Render separate widgets with GoogleReCaptcha component when inline

  scriptUrl.searchParams.append('render', isInline ? 'explicit' : recaptchaKey);
  scriptUrl.searchParams.append('onload', 'onloadRecaptchaCallback');

  if (recaptchaLang && recaptchaLang.length > 0) {
    scriptUrl.searchParams.append('hl', recaptchaLang);
  } // Load Script only if the API is not already set, if the key is set
  // and if the current form is enabled in the V3 configs


  var status = useScript(!apiIsReady && isEnabled ? scriptUrl : null); // Wait for config to be loaded and script to be ready

  var isLoading = configLoading || isEnabled && !apiIsReady && status !== 'ready'; // Render inline widget manually

  useEffect(function () {
    // Only render if container is set and API is available
    if (inlineContainer !== null && isInline && apiIsReady && widgetId === null) {
      // Avoid loading twice if already rendered
      if ('widgetId' in inlineContainer.dataset) {
        setWidgetId(inlineContainer.dataset.widgetId);
      } else {
        var id = globalThis.grecaptcha.render(inlineContainer, {
          sitekey: recaptchaKey,
          size: 'invisible'
        });
        setWidgetId(id);
        inlineContainer.dataset.widgetId = id;
      }
    }
  }, [apiIsReady, isInline, recaptchaKey, widgetId, inlineContainer]); // Callback sets API as ready

  if (!globalThis['recaptchaCallbacks'][formAction] && isEnabled) {
    globalThis['recaptchaCallbacks'][formAction] = function () {
      // Update non inline styles
      if (!isInline) {
        var floatingBadge = document.getElementsByClassName('grecaptcha-badge');

        if (floatingBadge && floatingBadge.length > 0) {
          floatingBadge[0].style.zIndex = 999;
        }
      }

      setApiIsReady(true);
    };
  } // Callback loops through each instance and set API as ready


  globalThis['onloadRecaptchaCallback'] = useCallback(function () {
    for (var key in globalThis['recaptchaCallbacks']) {
      globalThis['recaptchaCallbacks'][key]();
    } // Reset value after


    globalThis['recaptchaCallbacks'] = {};
  }, []); // Generate the object that will be sent with the request

  var generateReCaptchaData = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var token, result;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (!apiIsReady) {
              _context.next = 15;
              break;
            }

            _context.prev = 1;
            setIsGenerating(true);
            _context.next = 5;
            return globalThis.grecaptcha.execute(isInline ? widgetId : recaptchaKey, {
              action: formAction
            });

          case 5:
            token = _context.sent;
            result = {
              // TODO: Use Apollo Link middleware when solution is found
              context: {
                headers: _defineProperty({}, GOOGLE_RECAPTCHA_HEADER, token)
              }
            };
            setIsGenerating(false);
            return _context.abrupt("return", result);

          case 11:
            _context.prev = 11;
            _context.t0 = _context["catch"](1);
            // Log API error
            console.error(_context.t0);
            setIsGenerating(false);

          case 15:
            return _context.abrupt("return", {});

          case 16:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 11]]);
  })), [apiIsReady, formAction, isInline, recaptchaKey, widgetId]);
  var recaptchaWidgetProps = {
    containerElement: updateInlineContainerRef,
    shouldRender: !!(isEnabled && isInline && apiIsReady)
  };
  return {
    recaptchaLoading: isGenerating || isLoading,
    generateReCaptchaData: generateReCaptchaData,
    recaptchaWidgetProps: recaptchaWidgetProps
  };
};
/** JSDocs type definitions */

/**
 * Object type returned by the {@link useGoogleReCaptcha} hook.
 * It provides props data to use when attaching Google ReCaptcha V3 to a form.
 *
 * @typedef {Object} GoogleReCaptchaProps
 *
 * @property {Boolean} recaptchaLoading - Indicates if hook is loading data or loading the script.
 * @property {Function} generateReCaptchaData - The function to generate ReCaptcha Mutation data.
 * @property {Object} recaptchaWidgetProps - Props for the GoogleReCaptcha component.
 * @property {Function} recaptchaWidgetProps.containerElement - Container reference callback.
 * @property {Boolean} recaptchaWidgetProps.shouldRender - Checks if component should be rendered.
 */